/**
 * The rules of a saved login's fields (docs/credentials.md#what-is-stored), without zod: the
 * contracts' schemas (`api/credentials.ts`) are built on them, and the admin portal checks its form
 * with them, so both say the same thing and the portal's bundle takes no schemas.
 */

/** The longest password, in UTF-8 bytes, so it fits one RSA-OAEP block (docs/credentials.md#keys). */
export const MAX_SECRET_BYTES = 256;
/** The most hosts one login is filled on. */
export const MAX_CREDENTIAL_HOSTS = 20;
/** The most sender domains a login has. */
export const MAX_SENDER_DOMAINS = 5;
/** The longest code pattern a login may have. */
export const MAX_CODE_PATTERN_LENGTH = 200;
/** The longest label, system name, user ID and form selector. */
export const MAX_LABEL_LENGTH = 200;
export const MAX_SYSTEM_LENGTH = 100;
export const MAX_USERNAME_LENGTH = 256;
export const MAX_SELECTOR_LENGTH = 500;

const utf8 = new TextEncoder();

/** The length of a string in UTF-8 bytes. */
export function utf8Bytes(value: string): number {
  return utf8.encode(value).length;
}

const LABEL = "[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?";
/** A DNS name of at least two labels whose last starts with a letter, so never an IP address. */
const DOMAIN = `(?:${LABEL}\\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?`;
/** An exact host, lowercased. */
export const HOST_REGEX = new RegExp(`^${DOMAIN}$`);
/** An exact host or `*.` and a domain, lowercased. */
export const HOST_PATTERN_REGEX = new RegExp(`^(?:\\*\\.)?${DOMAIN}$`);

export const HOST_MESSAGE =
  "A host is a name such as portal.example.com, or *.example.com for every host under it; no scheme, port or path";
export const SENDER_DOMAIN_MESSAGE = "A mail domain such as steadily.com; no address, *. or scheme";
export const SECRET_MESSAGE = `A password is at most ${MAX_SECRET_BYTES} bytes (UTF-8)`;
export const CODE_PATTERN_MESSAGE = "Not a regular expression";
export const REPEATED_HOST_MESSAGE = "A host is listed twice";
export const REPEATED_DOMAIN_MESSAGE = "A domain is listed twice";

/** Whether a page's host is one of a login's hosts: equal, or under a `*.` pattern's domain. */
export function hostMatches(pattern: string, host: string): boolean {
  const wanted = pattern.toLowerCase();
  const actual = host.toLowerCase();
  if (wanted.startsWith("*.")) return actual.endsWith(wanted.slice(1));
  return actual === wanted;
}

/** Whether a page's host is one of a login's hosts. */
export function hostAllowed(patterns: readonly string[], host: string): boolean {
  return patterns.some((pattern) => hostMatches(pattern, host));
}

/** A login's host as the API keeps it: trimmed and lowercased. */
export function normalHost(value: string): string {
  return value.trim().toLowerCase();
}

/** What is wrong with a login's host, as typed; undefined when nothing is. */
export function hostProblem(value: string): string | undefined {
  const host = normalHost(value);
  return host.length <= 255 && HOST_PATTERN_REGEX.test(host) ? undefined : HOST_MESSAGE;
}

/** What is wrong with a sender domain, as typed; undefined when nothing is. */
export function senderDomainProblem(value: string): string | undefined {
  const domain = normalHost(value);
  return domain.length <= 253 && HOST_REGEX.test(domain) ? undefined : SENDER_DOMAIN_MESSAGE;
}

/** What is wrong with a password; undefined when nothing is. */
export function secretProblem(value: string): string | undefined {
  if (value.length === 0) return "A password cannot be empty";
  return utf8Bytes(value) <= MAX_SECRET_BYTES ? undefined : SECRET_MESSAGE;
}

/** What is wrong with a code pattern; undefined when nothing is ("" clears it). */
export function codePatternProblem(value: string): string | undefined {
  if (value.length > MAX_CODE_PATTERN_LENGTH) {
    return `A code pattern is at most ${MAX_CODE_PATTERN_LENGTH} characters`;
  }
  try {
    new RegExp(value, "i");
    return undefined;
  } catch {
    return CODE_PATTERN_MESSAGE;
  }
}

/** The hosts of Gmail's forwarding confirmation link (docs/credentials.md#emailed-codes). */
export const GMAIL_CONFIRMATION_HOSTS: readonly string[] = [
  "mail-settings.google.com",
  "mail.google.com",
  "isolated.mail.google.com",
];

/**
 * Whether a link from a forwarding confirmation may be shown as a link: `https`, on one of Gmail's
 * hosts, with no user, password or port. Anything else is shown as text, if at all.
 */
export function isGmailConfirmationLink(link: string): boolean {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return false;
  }
  return (
    url.protocol === "https:" &&
    url.username === "" &&
    url.password === "" &&
    url.port === "" &&
    GMAIL_CONFIRMATION_HOSTS.includes(url.hostname)
  );
}
